import React, { useState } from 'react';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import FormContext from '../FormContext';
import SingleCheckboxInput from '../SingleCheckboxInput';
import InputWrapper from '../InputWrapper';
import Question from '../Question';
import makeName from '../makeName';
import { FormattedMessage, useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { FormControl, FormGroup, FormHelperText, Paper } from '@material-ui/core';
import TranslatedMessage from '../TranslatedMessage';
import DistinguishTitle from '../DistinguishTitle';
import { FieldType, validatorFn, ValidatorType } from '../utils/validators/validateRequired';
import ValidationWrapper from './ValidationWrapper';

const useStyles = makeStyles(theme => ({
  noneOf: {
    padding: theme.spacing(0, 1.5),
    margin: theme.spacing(0, -1.5),
    transition: `all ${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut}`,
    background: 'transparent',
  },
  emphasized: {
    background: theme.palette.divider,
  },
}));

const CheckboxesInput = ({ label, question, checkboxes, omitnone, explanation, distinguish, validators }) => {
  const classes = useStyles();
  const intl = useIntl();

  const [error, setError] = useState(null);
  const rules = validators ? validators.map(type => validatorFn[type]?.(FieldType.Checkboxes) ?? (() => '')) : [];
  const required = !!validators?.find(e => e === ValidatorType.isRequired);

  return (
    <InputWrapper>
      {distinguish && <DistinguishTitle title={distinguish} />}
      <FormControl>
        <Question>
          {(label && <FormattedMessage id={label} defaultMessage={label} />) ||
            (question && <FormattedMessage id={question} defaultMessage={question} />) || (
              <FormattedMessage id="questionnaire.checkboxes.question" defaultMessage="Check all that apply" />
            )}
        </Question>

        <FormContext.Consumer>
          {({ name, value, onChange }) => {
            const handleChange = v => {
              const isComplete =
                Object.values(v || {})
                  .map(x => !!x)
                  .indexOf(true) > -1;
              onChange({ checkboxes: {...v, none: null}, progress: isComplete ? 1 : 0 });
            };

            return (
              <ValidationWrapper value={value?.checkboxes} rules={rules} error={error} setError={setError}>
                <FormGroup>
                  {checkboxes.map((checkbox, i) => {
                    const label = isArray(checkbox) ? checkbox[0] : checkbox;
                    const ifChecked = isArray(checkbox) ? checkbox[1] : undefined;

                    return (
                      <FormContext.Provider
                        key={i}
                        value={{
                          name: makeName(name, label),
                          value: get(value, ['checkboxes', label]),
                          onChange: v => handleChange({ ...get(value, 'checkboxes', {}), [label]: v }),
                        }}
                      >
                        <SingleCheckboxInput
                          label={intl.formatMessage({ id: label, defaultMessage: label })}
                          ifChecked={ifChecked}
                          required={required}
                          error={!!error}
                          omitNoneIfChecked
                        />
                      </FormContext.Provider>
                    );
                  })}
                  {!omitnone && (
                    <FormContext.Provider
                    value={{
                      name: makeName(name, 'none'),
                      value: get(value, 'progress', 0) > 0 && (isEmpty(get(value, 'checkboxes')) || get(value, 'checkboxes.none') === true),
                      onChange: v => {
                        if (v) {
                          const updatedCheckboxes = checkboxes.reduce((acc, checkbox) => {
                            const label = isArray(checkbox) ? checkbox[0] : checkbox;
                            acc[label] = false;
                            return acc;
                          }, {});
                          onChange({
                            checkboxes: { ...updatedCheckboxes, none: !get(value, 'checkboxes.none') },
                            progress: omitnone ? 0 : 1
                          });
                        } else {
                          onChange({ checkboxes: null, progress: 0 });
                        }
                      },
                    }}
                  >
                      <Paper
                        elevation={get(value, 'progress', 0) === 0 ? 1 : 0}
                        className={clsx(classes.noneOf, {
                          [classes.emphasized]: get(value, 'progress', 0) === 0,
                        })}
                      >
                        <SingleCheckboxInput
                          label={
                            <FormattedMessage id="questionnaire.checkboxes.none" defaultMessage="None of the above" />
                          }
                          required={required}
                          error={!!error}
                        />
                      </Paper>
                    </FormContext.Provider>
                  )}
                </FormGroup>
              </ValidationWrapper>
            );
          }}
        </FormContext.Consumer>

        {!!error && (
          <FormHelperText style={{ color: 'red' }}>
            <TranslatedMessage message={error} />
          </FormHelperText>
        )}

        {explanation && (
          <FormHelperText>
            <TranslatedMessage message={explanation} />
          </FormHelperText>
        )}
      </FormControl>
    </InputWrapper>
  );
};

CheckboxesInput.defaultValue = { checkboxes: {none: null}, progress: 0 };

export default CheckboxesInput;
